import { DataTable } from 'components';
import { DataTableColumn } from 'components/tables/data-table';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, useCallback } from 'react';
import { InvoiceItem } from 'types';

dayjs.extend(utc);

const DollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export const InvoiceDetailsList: FC<{
  showEdit: boolean;
  invoiceItems: InvoiceItem[];
  editClicked: (invoiceItem: InvoiceItem) => void;
}> = ({ invoiceItems, editClicked, showEdit }) => {
  const columnDefs: DataTableColumn[] = [
    {
      field: 'unitNumber',
      label: 'Unit No.',
      width: 150,
      className: 'font-weight-bold'
    },
    {
      field: 'sizeType',
      label: 'Size Type',
      width: 100
    },
    {
      field: 'gateInDate',
      label: 'Date In',
      type: 'date',
      getValueLabel: (value: Date) => (!value ? null : dayjs(value).utc().format('M/D/YYYY')),
      width: 100
    },
    {
      field: 'gateOutDate',
      label: 'Date Out',
      type: 'date',
      getValueLabel: (value: Date) => (!value ? null : dayjs(value).utc().format('M/D/YYYY')),
      width: 100
    },
    {
      field: 'totalDays',
      label: 'Total Days',
      type: 'number',
      width: 125
    },
    {
      field: 'chargedDays',
      label: 'Charged Days',
      type: 'number',
      width: 150,
      getValue: (value: InvoiceItem) => (value.chargedDays > 0 ? value.chargedDays : '0')
    },
    {
      field: 'storageCharge',
      label: 'Storage Charge',
      type: 'number',
      width: 175,
      getValueLabel: (value: number) => DollarFormatter.format(value)
    },
    {
      field: 'gateInCharge',
      label: 'Gate In',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'gateOutCharge',
      label: 'Gate Out',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'liftOnCharge',
      label: 'Lift On',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'liftOffCharge',
      label: 'Lift Off',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'digCharge',
      label: 'Dig',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'otherEventCharge',
      label: 'Misc',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    },
    {
      field: 'totalCharge',
      label: 'Total',
      className: 'font-weight-bold',
      type: 'number',
      getValueLabel: (value: number) => DollarFormatter.format(value),
      width: 100
    }
  ];

  const getInvoiceItems = useCallback(async () => {
    return invoiceItems;
  }, [invoiceItems]);

  return (
    <DataTable
      tableClass="table-container t-height-invoice-details"
      fetchData={getInvoiceItems}
      columns={columnDefs}
      initialColumns={columnDefs.map(col => {
        return { field: col.field };
      })}
      getRowActions={(row: InvoiceItem) => {
        return showEdit
          ? [
              <button className="btn-close" onClick={() => editClicked(row)}>
                <span className="material-icons icon-large">edit</span>
              </button>
            ]
          : [];
      }}
      clientSidePagination
      csvFilename="Invoice Items"
    />
  );
};
